import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  EyeIcon, 
  EyeSlashIcon,
  UserIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';

const Login = () => {
  console.log('🔐 Login - Componente renderizando...');
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiStatus, setApiStatus] = useState('unknown');

  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  console.log('🔐 Login - isAuthenticated:', isAuthenticated, 'authLoading:', authLoading);

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Testar conexão com API ao carregar
  useEffect(() => {
    testApiConnection();
  }, []);

  const testApiConnection = async () => {
    try {
      const result = await authService.testAuthConnection();
      setApiStatus(result.success ? 'online' : 'offline');
    } catch (error) {
      setApiStatus('offline');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro quando usuário digitar
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData);
      
      if (result.success) {
        // Redirecionar para a página que tentou acessar ou dashboard
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Erro ao fazer login');
      }
    } catch (error) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestApi = async () => {
    setIsLoading(true);
    try {
      const result = await authService.testAuthConnection();
      if (result.success) {
        alert(`✅ Conexão com API estabelecida!\n\nURL: ${result.url}\nStatus: ${result.status}`);
        setApiStatus('online');
      } else {
        alert(`❌ Falha na conexão com API!\n\nURL: ${result.url}\nErro: ${result.error}`);
        setApiStatus('offline');
      }
    } catch (error) {
      alert(`❌ Erro ao testar conexão: ${error.message}`);
      setApiStatus('offline');
    } finally {
      setIsLoading(false);
    }
  };

  // Não renderizar se ainda estiver verificando autenticação
  if (authLoading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Verificando autenticação...</h3>
          <p className="text-dark-text-secondary">Aguarde um momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <UserIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-dark-text">
            Dashboard PGS-IT
          </h2>
          <p className="mt-2 text-sm text-dark-text-secondary">
            Faça login para acessar o sistema
          </p>
        </div>

        {/* Status da API */}
        <div className="flex items-center justify-center gap-2">
          <div className="text-sm text-dark-text-secondary">
            Status da API:
          </div>
          {apiStatus === 'online' ? (
            <div className="flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4 text-success" />
              <span className="text-success text-sm font-medium">Online</span>
            </div>
          ) : apiStatus === 'offline' ? (
            <div className="flex items-center gap-1">
              <ExclamationTriangleIcon className="w-4 h-4 text-danger" />
              <span className="text-danger text-sm font-medium">Offline</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 border-2 border-warning border-t-transparent rounded-full animate-spin"></div>
              <span className="text-warning text-sm font-medium">Verificando...</span>
            </div>
          )}
        </div>

        {/* Formulário de Login */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Campo Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-dark-text mb-2">
                Usuário
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-dark-text-secondary" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  className="input-dark pl-10 w-full"
                  placeholder="Digite seu usuário"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Campo Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-text mb-2">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-dark-text-secondary" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-dark pl-10 pr-10 w-full"
                  placeholder="Digite sua senha"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-dark-text-secondary hover:text-dark-text" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-dark-text-secondary hover:text-dark-text" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mensagem de Erro */}
          {error && (
            <div className="bg-danger/10 border border-danger/20 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-danger flex-shrink-0" />
                <p className="text-danger text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Entrando...
                </>
              ) : (
                <>
                  <LockClosedIcon className="w-5 h-5" />
                  Entrar
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleTestApi}
              disabled={isLoading}
              className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Testando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                  Testar Conexão API
                </>
              )}
            </button>
          </div>
        </form>

        {/* Informações da API */}
        <div className="text-center">
          <p className="text-xs text-dark-text-secondary">
            API: <span className="font-mono">http://srv-mupa.ddns.net:5678</span>
          </p>
          <p className="text-xs text-dark-text-secondary mt-1">
            Dashboard: <span className="font-mono">http://localhost:5678</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
